import React, { useState } from "react";
import {
  FaCreditCard,
  FaFileAlt,
  FaPlay,
  FaRegCopy,
  FaCheck,
  FaChevronDown,
} from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "../../lib/axios";

const CustomSelect = ({ field, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <select
        name={field.name}
        value={value}
        onChange={() => {}}
        required={field.required}
        className="absolute inset-0 h-full w-full opacity-0"
        tabIndex={-1}
        style={{ pointerEvents: 'none' }}
      >
        <option value="">{field.label}</option>
        {field.options?.map((option, index) => (
          <option key={index} value={option}>{option}</option>
        ))}
      </select>
      
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between rounded-lg border border-black bg-white px-3 py-2.5 text-black transition-all focus:border-white/40 focus:outline-none"
      >
        <span className="text-sm">{value || field.label}</span>
        <FaChevronDown className={`text-xs text-black transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          <button
            type="button"
            onClick={() => {
              onChange({ target: { name: field.name, value: "" } });
              setIsOpen(false);
            }}
            className="w-full px-3 py-2 text-left text-sm text-black transition-colors hover:bg-[#bc1535] hover:!text-white"
          >
            {field.label}
          </button>
          {field.options?.map((option, index) => (
            <button
              key={index}
              type="button"
              onClick={() => {
                onChange({ target: { name: field.name, value: option } });
                setIsOpen(false);
              }}
              className="w-full px-3 py-2 text-left text-sm text-black transition-colors hover:bg-[#bc1535] hover:!text-white"
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ReachOutSection = ({
  title = "Become an Agency Partner",
  description = "Join other 500+ agencies who have partnered with us to grow.",
  formFields = [
    { name: "agencyName", label: "Agency Name", type: "text", required: true },
    { name: "fullName", label: "Full Name", type: "text", required: true },
    { name: "email", label: "Email Address", type: "email", required: true },
    { name: "mobile", label: "Mobile Number", type: "tel", required: true },
    { name: "city", label: "City", type: "text", required: true },
  ],
  category = "agency",
  id = null,
}) => {
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const isShareableCatalogSection =
    ["designing", "printing"].includes(category) && Boolean(id);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let endpoint = "/api/quotation/brand-endorsement";
      let payload = formData;

      if (category === "designing" || category === "printing") {
        endpoint = "/api/quotation/freelance-design";
        payload = {
          name: formData.name,
          phone: formData.mobile,
          email: formData.email,
          city: formData.city,
          type:
            category === "printing"
              ? "Marketing Solutions"
              : "Designing",
          params: {
            work_preference: formData.workPreference,
          },
        };
      }

      if (category === "influencer-marketing") {
        endpoint = "/api/quotation/freelance-design";
        payload = {
          name: formData.name,
          phone: formData.mobile,
          email: formData.email,
          city: formData.city,
          type: "Influencer Marketing",
          params: {
            influencer_type: formData.influencerType,
          },
        };
      }

      const response = await axios.post(endpoint, payload);
      if (response.status === 200) {
        alert("Application submitted successfully!");
        setFormData({});
      }
    } catch (error) {
      alert("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopySectionLink = async () => {
    if (!id || typeof window === "undefined") return;

    const shareUrl = `${window.location.origin}${window.location.pathname}#${id}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsLinkCopied(true);
      toast.success("Section link copied");
      window.setTimeout(() => setIsLinkCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  return (
    <section
      id={id}
      className="scroll-mt-28 bg-[#bc1535] py-8 sm:scroll-mt-36 sm:py-10"
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="mx-auto grid max-w-6xl items-start gap-8 md:grid-cols-2 md:gap-12">
          <div className="pt-1">
            <div className="mb-4">
              <h2 className="text-3xl font-bold leading-tight text-white sm:text-4xl">
                {title}
              </h2>
            </div>
            <div className="mb-6 sm:mb-8">
              {description.split("\n").map((line, index) => (
                <p
                  key={index}
                  className="text-base leading-6 text-white/90 sm:text-xl sm:leading-8"
                >
                  {line}
                </p>
              ))}
            </div>
            {isShareableCatalogSection ? (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={handleCopySectionLink}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-black px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-black/80"
                >
                  {isLinkCopied ? (
                    <FaCheck className="text-sm" />
                  ) : (
                    <FaRegCopy className="text-sm" />
                  )}
                  {isLinkCopied ? "Link Copied" : "Copy Section Link"}
                </button>
              </div>
            ) : null}
            {category === "agency" ? (
              <>
                <div className="flex justify-between mb-8">
                  <div className="text-center">
                    <div className="flex justify-center mb-4">
                      <FaCreditCard className="text-white text-4xl" />
                    </div>
                    <p className="text-white text-base">
                      Zero Investment <br />
                      Required
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="flex justify-center mb-4">
                      <FaFileAlt className="text-white text-4xl" />
                    </div>
                    <p className="text-white/90 text-base">
                      Revenue sharing <br />
                      agreement
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="flex justify-center mb-4">
                      <FaPlay className="text-white text-4xl" />
                    </div>
                    <p className="text-white/90 text-base">
                      Media Buying & Campaign <br /> Execution by Indorinama
                    </p>
                  </div>
                </div>
                <p className="text-sm text-white italic">
                  *For Digital Marketing Agencies, Creative Agencies, PR
                  Agencies, Outdoor Agencies etc.
                </p>
              </>
            ) : null}
          </div>

          <div className="rounded-2xl bg-white/10 p-4 backdrop-blur-sm sm:p-6 md:p-8">
            <form
              onSubmit={handleSubmit}
              className="mx-auto w-full sm:max-w-lg"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {formFields.map((field, index) =>
                  field.type === "select" ? (
                    <CustomSelect
                      key={index}
                      field={field}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <input
                      key={index}
                      type={field.type}
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (
                          (field.name === "city" ||
                            field.name === "agencyName" ||
                            field.name === "name" ||
                            field.name === "fullName") &&
                          !/^[a-zA-Z\s]*$/.test(value)
                        ) {
                          return;
                        }
                        if (
                          (field.name === "mobile" || field.name === "phone") &&
                          (!/^\d*$/.test(value) || value.length > 10)
                        ) {
                          return;
                        }
                        handleChange(e);
                      }}
                      onKeyDown={(e) => {
                        if (
                          (field.name === "mobile" || field.name === "phone") &&
                          (!/^\d*$/.test(e.key) ||
                            e.target.value.length >= 10) &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight" &&
                          e.key !== "Tab"
                        ) {
                          e.preventDefault();
                        }
                      }}
                      required={field.required}
                      className="w-full rounded-lg border border-black bg-white px-3 py-2.5 text-black placeholder-black transition-all focus:outline-none focus:border-white/40"
                      placeholder={field.label}
                      pattern={
                        field.name === "mobile"
                          ? "[0-9]*"
                          : field.name === "email"
                            ? "[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,}$"
                            : undefined
                      }
                    />
                  ),
                )}
              </div>
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-900 transition-colors duration-300 disabled:bg-gray-600"
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
